import React, { useState, useEffect, useCallback } from 'react';
import { getTree, getLevelCounts, getLevelSummary, getDirectReferrals, getLevelIncomeHistory } from '../../utils/api';
import Pagination, { usePagination } from '../Pagination';

const NetworkPage = ({ account, balance, price, f, fBC, vBC, BC, SYM }) => {
  const [levelCounts, setLevelCounts] = useState(null);
  const [summary, setSummary] = useState(null);
  const [directs, setDirects] = useState(null);
  const [tree, setTree] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState('');
  const [levelMembers, setLevelMembers] = useState([]);

  // Recent level income — server-side paginated + optional level filter (for debugging)
  const [income, setIncome] = useState({ entries: [], pagination: { total: 0, page: 1, pages: 1 } });
  const [incomePage, setIncomePage] = useState(1);
  const [incomeLevel, setIncomeLevel] = useState('');

  const tr = (a) => a ? `${a.slice(0, 6)}...${a.slice(-4)}` : '';

  const loadData = useCallback(async () => {
    if (!account) return;
    try {
      const [t, c, d, s] = await Promise.all([
        getTree(account), getLevelCounts(account), getDirectReferrals(account), getLevelSummary(account),
      ]);
      setTree(t); setLevelCounts(c); setDirects(d); setSummary(s);
    } catch (err) { console.error(err); }
  }, [account]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadIncome = useCallback(async () => {
    if (!account) return;
    try {
      const li = await getLevelIncomeHistory(account, incomePage, 15, incomeLevel);
      setIncome({ entries: li.entries || [], pagination: li.pagination || { total: 0, page: 1, pages: 1 } });
    } catch {}
  }, [account, incomePage, incomeLevel]);

  useEffect(() => { loadIncome(); }, [loadIncome]);

  useEffect(() => {
    if (selectedLevel && tree?.tree?.[selectedLevel]) {
      setLevelMembers(tree.tree[selectedLevel]);
    } else {
      setLevelMembers([]);
    }
  }, [selectedLevel, tree]);

  const totalTeam = summary?.totalTeam ?? levelCounts?.totalMembers ?? 0;
  const maxLv = summary?.maxLevel || price?.distributeLevels || 10;

  const memPg = usePagination(levelMembers, 15);

  return (
    <div className="fade-in">

      {/* Level Summary */}
      <div className="d-section">
        <h3>Level Summary <span className="badge">{totalTeam} total team</span></h3>
        <div className="table-wrap"><table className="table">
          <thead><tr><th>Level</th><th>Team Size</th><th>Team Invested</th><th>Income %</th><th>Income Earned</th><th>Missed (inactive)</th></tr></thead>
          <tbody>
            <tr className="table-row-highlight">
              <td><strong>Direct Referrals</strong></td>
              <td className="mono">{directs?.directCount || 0}</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
            </tr>
            {Array.from({ length: summary?.maxLevel || price?.distributeLevels || 10 }, (_, i) => i + 1).map(lv => {
              const row = summary?.levels?.[lv] || {};
              const count = row.count ?? (levelCounts?.levels?.[lv] || 0);
              const invested = row.invested || 0;
              const pct = row.percent ?? (price?.levelPercentages?.[lv] || 0);
              const earned = row.incomeEarned || 0;
              const missed = row.missed || 0;
              return (
                <tr key={lv}>
                  <td>Level {lv}</td>
                  <td className="mono">{count}</td>
                  <td className="mono">{invested > 0 ? vBC(invested, 4) : <span className="text-3">0.00</span>}</td>
                  <td className="mono">{pct}%</td>
                  <td className="mono">{earned > 0 ? <span className="text-green">{vBC(earned, 4)}</span> : <span className="text-3">0.00</span>}</td>
                  <td className="mono">{missed > 0 ? <span className="text-red">{vBC(missed, 4)}</span> : <span className="text-3">0.00</span>}</td>
                </tr>
              );
            })}
            <tr className="table-row-highlight">
              <td><strong>Total</strong></td>
              <td className="mono"><strong>{totalTeam}</strong></td>
              <td className="mono"><strong>{vBC(summary?.totalInvested || 0, 4)}</strong></td>
              <td>—</td>
              <td className="mono"><strong className="text-accent">{vBC(summary?.totalIncome ?? balance?.levelIncomeEarned, 4)}</strong></td>
              <td className="mono"><strong className="text-red">{vBC(summary?.totalMissed || 0, 4)}</strong></td>
            </tr>
          </tbody>
        </table></div>
      </div>

      {/* Level Members */}
      <div className="d-section">
        <h3>Level Members</h3>
        <div className="d-level-select">
          <select value={selectedLevel} onChange={e => setSelectedLevel(e.target.value)} className="input">
            <option value="">Select Level</option>
            {Array.from({ length: maxLv }, (_, i) => i + 1).map(lv => (
              <option key={lv} value={lv}>Level {lv} ({summary?.levels?.[lv]?.count ?? levelCounts?.levels?.[lv] ?? 0} members)</option>
            ))}
          </select>
        </div>

        {selectedLevel && (
          <>
          <div className="table-wrap"><table className="table">
            <thead><tr><th>S.No.</th><th>Referral Code</th><th>Upline (Referral Code)</th><th>Wallet</th><th>Total Invested</th></tr></thead>
            <tbody>
              {memPg.total > 0 ? memPg.pageItems.map((m, i) => (
                <tr key={m.user}>
                  <td>{(memPg.page - 1) * memPg.perPage + i + 1}</td>
                  <td className="mono">{m.referralCode || m.user}</td>
                  <td className="mono">{m.uplineReferralCode || m.parent || '—'}</td>
                  <td className="mono">{tr(m.userWallet)}</td>
                  <td className="mono">{m.totalDeposit > 0 ? vBC(m.totalDeposit, 4) : <span className="text-3">0.00</span>}</td>
                </tr>
              )) : (
                <tr><td colSpan="5" className="text-3" style={{ textAlign: 'center' }}>No members at this level</td></tr>
              )}
            </tbody>
          </table></div>
          <Pagination page={memPg.page} pages={memPg.pages} total={memPg.total} perPage={memPg.perPage} onPage={memPg.setPage} />
          </>
        )}
      </div>

      {/* Recent Level Income Entries — server paginated + level filter */}
      <div className="d-section">
        <h3>Recent Level Income</h3>
        <div className="d-level-select" style={{ marginBottom: 12 }}>
          <select value={incomeLevel} onChange={e => { setIncomeLevel(e.target.value); setIncomePage(1); }} className="input">
            <option value="">All levels</option>
            {Array.from({ length: maxLv }, (_, i) => i + 1).map(lv => (
              <option key={lv} value={lv}>Level {lv}</option>
            ))}
          </select>
        </div>
        <div className="table-wrap"><table className="table">
          <thead><tr><th>Date</th><th>Level</th><th>From (Referral Code)</th><th>Amount</th></tr></thead>
          <tbody>
            {income.entries.length > 0 ? income.entries.map(entry => (
              <tr key={entry._id}>
                <td>{new Date(entry.createdAt).toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</td>
                <td>L{entry.level}</td>
                <td className="mono">{entry.triggerReferralCode || entry.triggerRoboId || '—'}</td>
                <td className="mono text-green">{vBC(entry.usdtAmount, 4)}</td>
              </tr>
            )) : (
              <tr><td colSpan="4" className="text-3" style={{ textAlign: 'center' }}>No level income{incomeLevel ? ` at level ${incomeLevel}` : ''} yet</td></tr>
            )}
          </tbody>
        </table></div>
        <Pagination
          page={income.pagination.page || 1}
          pages={income.pagination.pages || 1}
          total={income.pagination.total || 0}
          perPage={15}
          onPage={(p) => setIncomePage(p)}
        />
      </div>
    </div>
  );
};

export default NetworkPage;